/*
 * File: cratePasswordApi.ts
 *
 * CraetePasswordApi: RTK Query APIs for the create password process.
 *
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AxiosError } from "axios";
import { PageContent } from "../types";
import {
  buildStrippedJsonFromValues,
  getRecaptchaToken,
  initAndGetSeonPayload,
  transformAxiosToFetchBaseQueryErrorWithLocationRedirect,
} from "../utils";
import {
  apiOnboardingKeyName,
  apiOnboardingKeyValue,
  endpoints,
  getCreatePasswordFetcher,
} from "./ApiConfig";

export const createPasswordApi = createApi({
  reducerPath: "createPasswordApi",
  baseQuery: fetchBaseQuery({
    baseUrl: endpoints.createPassword,
    prepareHeaders: headers => {
      if (apiOnboardingKeyName && apiOnboardingKeyValue) {
        headers.set(apiOnboardingKeyName, apiOnboardingKeyValue);
      }
      return headers;
    },
  }),
  tagTypes: ["CreatePassword"],
  endpoints: builder => ({
    startPasswordCreate: builder.mutation<
      PageContent,
      {
        queryString: string;
        analyticsId?: string;
      }
    >({
      async queryFn({ queryString, analyticsId }) {
        try {
          await initAndGetSeonPayload();
          const res = await getCreatePasswordFetcher(
            undefined,
            undefined,
            true,
          ).post(
            "/start",
            {
              queryString,
              analyticsId,
            },
            {
              withCredentials: true,
            },
          );
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
            true,
          );
          return { error };
        }
      },
    }),
    nextPasswordCreate: builder.mutation<
      PageContent,
      {
        stepId?: number;
        data: PageContent;
        values: Record<string, unknown>;
      }
    >({
      async queryFn(arg) {
        try {
          const gRecaptchaResponse = await getRecaptchaToken();
          const seonPayload = await initAndGetSeonPayload();
          const parsedData = buildStrippedJsonFromValues({
            pageContent: arg.data,
            values: arg.values,
            filterByVisibility: true,
          });
          const res = await getCreatePasswordFetcher({
            gRecaptchaResponse,
            seonPayload,
          }).patch("/next", parsedData, { withCredentials: true });

          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
    backPasswordCreate: builder.mutation<
      PageContent,
      { stepId?: number; processId: string }
    >({
      async queryFn({ processId }) {
        const headers:
          | Headers
          | string[][]
          | Record<string, string | undefined> = {};
        try {
          const res = await getCreatePasswordFetcher().get(
            `back/${processId}`,
            {
              withCredentials: true,
              headers,
            },
          );
          if (res?.data && !res.data.step) {
            throw new Error("Step not found");
          }
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
            true,
          );
          return { error };
        }
      },
    }),
  }),
});

export const {
  useStartPasswordCreateMutation,
  useBackPasswordCreateMutation,
  useNextPasswordCreateMutation,
} = createPasswordApi;

export const allCreatePasswordFulfilledMatchers = [
  createPasswordApi.endpoints.backPasswordCreate.matchFulfilled,
  createPasswordApi.endpoints.nextPasswordCreate.matchFulfilled,
  createPasswordApi.endpoints.startPasswordCreate.matchFulfilled,
];

export const allCreatePasswordRejectedMatchers = [
  createPasswordApi.endpoints.backPasswordCreate.matchRejected,
  createPasswordApi.endpoints.nextPasswordCreate.matchRejected,
  createPasswordApi.endpoints.startPasswordCreate.matchRejected,
];

export const allCreatePasswordPendingMatchers = [
  createPasswordApi.endpoints.backPasswordCreate.matchPending,
  createPasswordApi.endpoints.nextPasswordCreate.matchPending,
  createPasswordApi.endpoints.startPasswordCreate.matchPending,
];
